import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Lock, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Initialize Stripe with LIVE publishable key
const stripePromise = loadStripe('pk_live_51S9VQQBE9gkpGXbbazmA9D726piT3QN7MtiuyHsz49QVdS1vZlajwsTlaRMjpnxOcxhkRTzzXlniiGmhfCDaHOXH00drnC3Hyl');

/**
 * Reusable Stripe Card Form Component
 * Handles Stripe payment method setup with proper security
 */
const CardSetupForm = ({ onSuccess, onCancel, showUpgradeNotice = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    // Create setup intent when component mounts
    const createSetupIntent = async () => {
      try {
        const response = await axios.post(
          `${API}/payments/setup-intent`,
          {},
          { withCredentials: true }
        );
        setClientSecret(response.data.client_secret);
      } catch (error) {
        console.error('Error creating setup intent:', error);
        toast.error('Failed to initialize payment setup');
      }
    };

    createSetupIntent();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardElement);

      // REAL STRIPE PAYMENT METHOD SETUP
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        // Enhanced error messaging for common issues
        if (error.code === 'incomplete_zip' || error.message?.includes('postal code') || error.message?.includes('ZIP')) {
          throw new Error('Please enter a valid postal/ZIP code to continue.');
        }
        throw new Error(error.message);
      }

      // Save real Stripe payment method to backend
      const response = await axios.post(
        `${API}/payment/setup-method`,
        { payment_method_id: setupIntent.payment_method },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(`Payment method added! Card ending in ${response.data.card_last4}`);
        onSuccess(response.data);
      } else {
        toast.error('Failed to save payment method');
      }

    } catch (error) {
      console.error('Payment method setup error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to add payment method');
    }

    setLoading(false);
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: false, // Explicitly show postal code field (required for payments)
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
      {showUpgradeNotice && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-start">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1 text-sm sm:text-base">First-Time Setup</h4>
              <p className="text-xs sm:text-sm text-blue-800 break-words">
                To access premium services, please add a payment method. This will upgrade your account to A la Carte.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
        <div className="flex items-start">
          <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-1 text-sm sm:text-base">Secure Payment</h4>
            <p className="text-xs sm:text-sm text-blue-800">
              Your card information is encrypted and secure. We use Stripe for payment processing.
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
          <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 inline mr-2" />
          Card Information
        </label>
        <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-white">
          <CardElement options={cardElementOptions} />
        </div>
        <p className="text-[10px] sm:text-xs text-gray-500 mt-2">
          ⓘ All fields including postal/ZIP code are required for payment processing
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4 sm:gap-0">
        <button
          type="submit"
          disabled={!stripe || !clientSecret || loading}
          className="w-full sm:flex-1 bg-blue-600 text-white py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Adding Card...
            </>
          ) : (
            'Add Payment Method'
          )}
        </button>
        
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 disabled:opacity-50 text-sm sm:text-base"
        >
          Cancel
        </button>
      </div>

      {showUpgradeNotice && (
        <div className="pt-3 sm:pt-4 border-t border-gray-200">
          <p className="text-[10px] sm:text-xs text-gray-500 text-center break-words">
            Adding a payment method will upgrade your account to A la Carte status.
          </p>
        </div>
      )}
    </form>
  );
};

/**
 * Reusable Stripe Payment Form Wrapper
 * Can be used in modals or standalone
 */
const StripePaymentForm = ({ onSuccess, onCancel, showUpgradeNotice = false }) => {
  return (
    <Elements stripe={stripePromise}>
      <CardSetupForm 
        onSuccess={onSuccess}
        onCancel={onCancel}
        showUpgradeNotice={showUpgradeNotice}
      />
    </Elements>
  );
};

export default StripePaymentForm;
export { stripePromise, CardSetupForm };
